// frontend/src/pages/admin/Settings.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { 
  FiSave, 
  FiUpload, 
  FiImage,
  FiInfo,
  FiPhone,
  FiShare2,
  FiMonitor,
  FiLayout
} from 'react-icons/fi';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Settings state
  const [settings, setSettings] = useState({});
  
  // File uploads
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  // Tabs configuration
  const tabs = [
    { id: 'company', label: 'Компанийн мэдээлэл', icon: FiInfo },
    { id: 'contact', label: 'Холбоо барих', icon: FiPhone },
    { id: 'social', label: 'Сошиал медиа', icon: FiShare2 },
    { id: 'logos', label: 'Лого', icon: FiMonitor },
    { id: 'banners', label: 'Баннерууд', icon: FiLayout }
  ];

  // Load settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/settings');
      
      if (data.success) {
        setSettings(data.data.settings);
      }
    } catch (error) {
      console.error('Settings load error:', error);
      showMessage('error', 'Тохиргоо татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      const { data } = await axios.put('/api/settings', { settings });
      
      if (data.success) {
        showMessage('success', 'Тохиргоо амжилттай хадгалагдлаа');
      }
    } catch (error) {
      console.error('Save settings error:', error);
      showMessage('error', 'Тохиргоо хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (type, file) => {
    try {
      setUploadingLogo(true);
      
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('type', type);
      
      const { data } = await axios.post('/api/settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (data.success) {
        showMessage('success', 'Лого амжилттай солигдлоо');
        loadSettings(); // Reload to get new logo URL
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      showMessage('error', 'Лого upload хийхэд алдаа гарлаа');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerUpload = async (bannerKey, file) => {
    try {
      setUploadingBanner(true);
      
      const formData = new FormData();
      formData.append('banner', file);
      formData.append('banner_key', bannerKey);
      
      const { data } = await axios.post('/api/settings/upload-banner', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (data.success) {
        showMessage('success', 'Баннер амжилттай солигдлоо');
        loadSettings();
      }
    } catch (error) {
      console.error('Banner upload error:', error);
      showMessage('error', 'Баннер upload хийхэд алдаа гарлаа');
    } finally {
      setUploadingBanner(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Системийн тохиргоо</h1>
        <p className="text-gray-600 mt-1">Вэбсайтын ерөнхий тохиргоо удирдах</p>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b">
          <div className="flex space-x-4 px-6 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-2 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Icon size={18} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Company Info Tab */}
          {activeTab === 'company' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Компанийн мэдээлэл</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Компанийн нэр
                </label>
                <input
                  type="text"
                  value={settings.company_name || ''}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Компанийн нэр оруулах"
                />
                <p className="text-xs text-gray-500 mt-1">Вэбсайтын гарчигт харагдана</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Уриа үг / Тайлбар
                </label>
                <textarea
                  value={settings.company_slogan || ''}
                  onChange={(e) => handleInputChange('company_slogan', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Компанийн уриа үг эсвэл товч тайлбар"
                />
                <p className="text-xs text-gray-500 mt-1">Footer хэсэгт харагдана</p>
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Компаний нэрийн доорх уриа үг
              </label>
              <input
                type="text"
                value={settings.site_tagline || ''}
                onChange={(e) => handleInputChange('site_tagline', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Жишээ: Онлайн дэлгүүр"
              />
              <p className="text-xs text-gray-500 mt-1">
                Компанийн нэрийн доор гарах богино текст (header болон footer дээр)
              </p>
            </div>
            </div>
          )}

          {/* Contact Info Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Холбоо барих мэдээлэл</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Утасны дугаар
                  </label>
                  <input
                    type="text"
                    value={settings.contact_phone || ''}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+976 1122-3344"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Имэйл хаяг
                  </label>
                  <input
                    type="email"
                    value={settings.contact_email || ''}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="info@example.mn"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Байршлын хаяг
                </label>
                <input
                  type="text"
                  value={settings.contact_address || ''}
                  onChange={(e) => handleInputChange('contact_address', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="УБ хот, Сүхбаатар дүүрэг"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ажлын цаг
                  </label>
                  <input
                    type="text"
                    value={settings.working_hours || ''}
                    onChange={(e) => handleInputChange('working_hours', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="09:00 - 18:00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ажлын өдрүүд
                  </label>
                  <input
                    type="text"
                    value={settings.working_days || ''}
                    onChange={(e) => handleInputChange('working_days', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Даваа - Баасан"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Social Media Tab */}
          {activeTab === 'social' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Сошиал медиа хаягууд</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facebook
                </label>
                <input
                  type="url"
                  value={settings.social_facebook || ''}
                  onChange={(e) => handleInputChange('social_facebook', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://facebook.com/yourpage"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram
                </label>
                <input
                  type="url"
                  value={settings.social_instagram || ''}
                  onChange={(e) => handleInputChange('social_instagram', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://instagram.com/yourpage"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Twitter
                </label>
                <input
                  type="url"
                  value={settings.social_twitter || ''}
                  onChange={(e) => handleInputChange('social_twitter', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://twitter.com/yourpage"
                />
              </div>
            </div>
          )}

          {/* Logos Tab */}
          {activeTab === 'logos' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Лого зургууд</h3>
              
              {/* Header Logo */}
              <div className="border rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Header лого
                </label>
                
                {settings.header_logo && (
                  <div className="mb-3 p-4 bg-gray-50 rounded-lg inline-block">
                    <img 
                      src={`${process.env.REACT_APP_API_URL}${settings.header_logo}`}
                      alt="Header Logo"
                      className="h-16 object-contain"
                    />
                  </div>
                )}
                
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                  <FiUpload />
                  <span>{uploadingLogo ? 'Уншиж байна...' : 'Зураг солих'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && handleLogoUpload('header', e.target.files[0])}
                    disabled={uploadingLogo}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">PNG, JPG, SVG (max 5MB)</p>
              </div>

              {/* Footer Logo */}
              <div className="border rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Footer лого
                </label>
                
                {settings.footer_logo && (
                  <div className="mb-3 p-4 bg-gray-50 rounded-lg inline-block">
                    <img 
                      src={`${process.env.REACT_APP_API_URL}${settings.footer_logo}`}
                      alt="Footer Logo"
                      className="h-16 object-contain"
                    />
                  </div>
                )}
                
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                  <FiUpload />
                  <span>{uploadingLogo ? 'Уншиж байна...' : 'Зураг солих'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && handleLogoUpload('footer', e.target.files[0])}
                    disabled={uploadingLogo}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">PNG, JPG, SVG (max 5MB)</p>
              </div>
            </div>
          )}

          {/* Banners Tab */}
          {activeTab === 'banners' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Sidebar баннерууд</h3>
              
              {/* Banner 1 */}
              <div className="border rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sidebar баннер 1
                </label>
                
                {settings.sidebar_banner_1 && (
                  <div className="mb-3 p-4 bg-gray-50 rounded-lg inline-block">
                    <img 
                      src={`${process.env.REACT_APP_API_URL}${settings.sidebar_banner_1}`}
                      alt="Banner 1"
                      className="h-32 object-contain"
                    />
                  </div>
                )}
                
                <div className="space-y-3">
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                    <FiImage />
                    <span>{uploadingBanner ? 'Уншиж байна...' : 'Зураг солих'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleBannerUpload('sidebar_banner_1', e.target.files[0])}
                      disabled={uploadingBanner}
                    />
                  </label>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Холбоос (дарахад очих хаяг)
                    </label>
                    <input
                      type="url"
                      value={settings.sidebar_banner_1_link || ''}
                      onChange={(e) => handleInputChange('sidebar_banner_1_link', e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Banner 2 */}
              <div className="border rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sidebar баннер 2
                </label>
                
                {settings.sidebar_banner_2 && (
                  <div className="mb-3 p-4 bg-gray-50 rounded-lg inline-block">
                    <img 
                      src={`${process.env.REACT_APP_API_URL}${settings.sidebar_banner_2}`}
                      alt="Banner 2"
                      className="h-32 object-contain"
                    />
                  </div>
                )}
                
                <div className="space-y-3">
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                    <FiImage />
                    <span>{uploadingBanner ? 'Уншиж байна...' : 'Зураг солих'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleBannerUpload('sidebar_banner_2', e.target.files[0])}
                      disabled={uploadingBanner}
                    />
                  </label>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Холбоос (дарахад очих хаяг)
                    </label>
                    <input
                      type="url"
                      value={settings.sidebar_banner_2_link || ''}
                      onChange={(e) => handleInputChange('sidebar_banner_2_link', e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="border-t px-6 py-4 bg-gray-50 flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave />
            <span>{saving ? 'Хадгалж байна...' : 'Хадгалах'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;